import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge, Button, Card, Field, Input, Link, MessageBar, MessageBarBody, Spinner, Subtitle2, Text, makeStyles, tokens,
} from '@fluentui/react-components';
import { api } from '../../api/client';
import {
  NEEDS_CUSTOMER, SETUP_MODE_OPTIONS, SITE_OWNER_HINT, STEP_LABELS, STEP_ORDER, isSiteOwnerLike, type DeploymentSession,
  type ProductManifest, type StepId, type StepState,
} from '../../api/types';
import { connectCustomer, currentCustomer, customerTokens, type CustomerConnection, type TokenOptions } from '../../customer/customerAuth';
import { AzurePackPanel } from './AzurePackPanel';

const useStyles = makeStyles({
  stack: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  row: { display: 'flex', gap: tokens.spacingHorizontalM, alignItems: 'center', flexWrap: 'wrap' },
  step: { display: 'grid', gridTemplateColumns: '28px 1fr auto', gap: tokens.spacingHorizontalM, alignItems: 'start',
          padding: `${tokens.spacingVerticalS} 0`, borderBottom: `1px solid ${tokens.colorNeutralStroke2}` },
  num: { color: tokens.colorNeutralForeground3, fontWeight: 600 },
  msg: { color: tokens.colorNeutralForeground2, display: 'block' },
});

const badge: Record<StepState['status'], 'informative' | 'brand' | 'success' | 'danger' | 'subtle'> = {
  pending: 'subtle', running: 'brand', done: 'success', failed: 'danger', skipped: 'informative',
};

/** First step that is not done/skipped — the only one that may run next. */
function nextStep(s: DeploymentSession): StepId | undefined {
  return STEP_ORDER.find((id) => !['done', 'skipped'].includes(s.steps[id].status));
}

export function SessionView({ id }: { id: string }) {
  const styles = useStyles();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const session = useQuery({ queryKey: ['session', id], queryFn: () => api<DeploymentSession>(`sessions/${id}`) });
  const products = useQuery({ queryKey: ['products'], queryFn: () => api<ProductManifest[]>('products') });
  const manifest = products.data?.find((p) => p.productId === session.data?.productId);
  const pack = manifest?.azure.pack;

  const [customer, setCustomer] = useState<CustomerConnection | null>(currentCustomer());
  const [connectError, setConnectError] = useState<string | null>(null);
  const [siteUrl, setSiteUrl] = useState('');
  const [owner, setOwner] = useState<string | null>(null);   // null = not edited; shows the session's value
  const [outputs, setOutputs] = useState<Record<string, string>>({});

  const run = useMutation({
    mutationFn: async ({ step, input, tokens: tokenOpts }: { step: StepId; input?: Record<string, unknown>; tokens?: TokenOptions }) =>
      api<DeploymentSession>(`sessions/${id}/steps/${step}`, {
        body: input ?? {},
        // The complete step also gets the admin's tokens when they are still signed in (silently, no
        // popup), so it can take back DM's temporary Azure consent; without them that is recorded.
        customer: NEEDS_CUSTOMER.includes(step) ? await customerTokens(tokenOpts)
          : step === 'complete' && currentCustomer() ? await customerTokens({ silentOnly: true }).catch(() => undefined) : undefined,
      }),
    onSuccess: (s) => qc.setQueryData(['session', id], s),
    // The API records failures in the session before returning the error — reload to show them.
    onError: () => qc.invalidateQueries({ queryKey: ['session', id] }),
  });

  const updateSite = useMutation({
    mutationFn: (url: string) => api<DeploymentSession>(`sessions/${id}/site`, { body: { siteUrl: url } }),
    onSuccess: (s) => { qc.setQueryData(['session', id], s); setSiteUrl(''); },
  });

  // Correcting the owner before the site step is done (e.g. after "No one with the address … was found").
  const updateOwner = useMutation({
    mutationFn: (value: string) => api<DeploymentSession>(`sessions/${id}/site`, { body: { siteOwner: value.trim() } }),
    onSuccess: (s) => { qc.setQueryData(['session', id], s); setOwner(null); },
  });

  const abandon = useMutation({
    mutationFn: async () => api<DeploymentSession>(`sessions/${id}/abandon`, {
      method: 'POST', body: {},
      customer: currentCustomer() ? await customerTokens({ silentOnly: true }).catch(() => undefined) : undefined,
    }),
    onSuccess: () => navigate('/deploy'),
  });

  if (session.isLoading) return <Spinner label="Loading deployment…" />;
  if (session.error) return <MessageBar intent="error"><MessageBarBody>{(session.error as Error).message}</MessageBarBody></MessageBar>;
  const s = session.data!;
  const next = nextStep(s);
  const busy = run.isPending;
  const setupChoice = SETUP_MODE_OPTIONS.find((o) => o.value === s.setupMode)?.label ?? s.setupMode;
  // Written by the complete step; absent on deployments completed before the setup link existed.
  const done = s.steps.complete.detail as { setupUrl?: string; whatHappens?: string; openAs?: string } | undefined;
  const ownerValue = owner ?? s.siteOwner ?? '';
  const ownerOk = !ownerValue.trim() || isSiteOwnerLike(ownerValue);

  async function connect() {
    setConnectError(null);
    try {
      const c = await connectCustomer();
      if (c.tenantId !== s.tenantId) {
        setConnectError(`That account belongs to tenant ${c.tenantId}; this licence is for ${s.tenantId}. Sign in with an admin of the licensed tenant.`);
        return;
      }
      setCustomer(c);
    } catch (e) {
      setConnectError((e as Error).message);
    }
  }

  function runNext() {
    if (!next) return;
    if (next === 'azure' && pack) {
      // Retry of a failed Azure step: re-submits (option A) with the stored target, or re-checks (option B).
      run.mutate({ step: 'azure', tokens: s.azure?.mode === 'portal' ? {} : { azure: true, appRegistration: !!pack.appRegistration } });
      return;
    }
    if (next === 'azure') {
      run.mutate({ step: 'azure', input: { outputs } });
      return;
    }
    run.mutate({ step: next });
  }

  const needsCustomer = next && NEEDS_CUSTOMER.includes(next) && !customer;
  // A product with an Azure pack drives the azure step from its own panel; the main button is only its Retry.
  const packDrivesStep = next === 'azure' && !!pack && s.steps.azure.status !== 'failed';

  return (
    <div className={styles.stack}>
      <Card>
        <div className={styles.row}>
          <Subtitle2>{s.displayName} {s.version}</Subtitle2>
          <Badge appearance="tint" color={s.status === 'completed' ? 'success' : s.status === 'abandoned' ? 'subtle' : 'brand'}>{s.status}</Badge>
        </div>
        <Text>Customer: {s.customerName || '—'} · Tenant {s.tenantId}</Text>
        <Text>Site: {s.siteUrl ? <Link href={s.siteUrl} target="_blank" rel="noreferrer">{s.siteUrl}</Link> : 'set after the admin signs in'}</Text>
        <Text>Setup: {setupChoice}</Text>
        <Text>Site owner: {s.siteOwner || 'the Global Admin who signs in'}</Text>
        <Text>Started by {s.createdBy.name} · {new Date(s.createdAt).toLocaleString()}</Text>
      </Card>

      <Card>
        <Subtitle2>Customer Global Admin</Subtitle2>
        {customer
          ? <Text>Signed in as <b>{customer.username}</b> ({customer.sharePointHost}). Their sign-in stays in this tab only.</Text>
          : <Text>The customer&apos;s Global Admin signs in here — on this screen, with you. Their account must belong to the licensed tenant.</Text>}
        <div className={styles.row}>
          <Button onClick={connect} disabled={busy}>{customer ? 'Sign in a different admin' : 'Sign in customer admin'}</Button>
        </div>
        {connectError && <MessageBar intent="error"><MessageBarBody>{connectError}</MessageBarBody></MessageBar>}
      </Card>

      {s.steps.connect.status === 'done' && s.steps.site.status !== 'done' && (
        <Card>
          <Subtitle2>SharePoint site</Subtitle2>
          <div className={styles.row}>
            <Field label="Site URL (created if it does not exist)">
              <Input style={{ minWidth: 420 }} value={siteUrl || s.siteUrl} onChange={(_, d) => setSiteUrl(d.value)} />
            </Field>
            <Button disabled={!siteUrl || siteUrl === s.siteUrl || updateSite.isPending} onClick={() => updateSite.mutate(siteUrl)}>Use this site</Button>
          </div>
          {updateSite.error && <MessageBar intent="error"><MessageBarBody>{(updateSite.error as Error).message}</MessageBarBody></MessageBar>}
          <div className={styles.row}>
            <Field label="Site owner (optional)" hint={SITE_OWNER_HINT}
                   validationMessage={ownerOk ? undefined : 'Enter the person\'s email or sign-in name, e.g. name@company.com'}>
              <Input type="email" style={{ minWidth: 420 }} value={ownerValue} onChange={(_, d) => setOwner(d.value)}
                     placeholder="name@company.com" />
            </Field>
            <Button disabled={owner === null || !ownerOk || owner.trim() === (s.siteOwner ?? '') || updateOwner.isPending}
                    onClick={() => updateOwner.mutate(ownerValue)}>Use this owner</Button>
          </div>
          {updateOwner.error && <MessageBar intent="error"><MessageBarBody>{(updateOwner.error as Error).message}</MessageBarBody></MessageBar>}
        </Card>
      )}

      <Card>
        <Subtitle2>Steps</Subtitle2>
        {STEP_ORDER.map((stepId, i) => {
          const st = s.steps[stepId];
          return (
            <div key={stepId} className={styles.step}>
              <span className={styles.num}>{i + 1}</span>
              <div>
                <Text weight="semibold">{STEP_LABELS[stepId]}</Text>
                {st.message && <Text size={200} className={styles.msg}>{st.message}</Text>}
              </div>
              <Badge appearance="filled" color={badge[st.status]}>{busy && next === stepId ? 'running…' : st.status}</Badge>
            </div>
          );
        })}

        {next === 'azure' && pack && customer && (
          <AzurePackPanel session={s} pack={pack} busy={busy}
                          onRun={(input, tokenOpts) => run.mutate({ step: 'azure', input, tokens: tokenOpts })} />
        )}
        {next === 'azure' && pack && !customer && <Text>Sign in the customer admin to deploy the Azure backend.</Text>}

        {next === 'azure' && !pack && manifest && (manifest.azure.required || manifest.azure.optional) && (
          <div className={styles.stack}>
            <Text>
              The customer deploys this product&apos;s Azure backend with the handoff pack
              {manifest.azure.guideUrl && <> (<Link href={manifest.azure.guideUrl} target="_blank" rel="noreferrer">guide</Link>)</>}.
              Enter what their deployment reports, then verify.
            </Text>
            {(manifest.azure.outputs ?? []).map((o) => (
              <Field key={o.key} label={o.label} hint={o.help}>
                <Input value={outputs[o.key] ?? ''} onChange={(_, d) => setOutputs({ ...outputs, [o.key]: d.value })} />
              </Field>
            ))}
            {manifest.azure.optional && (
              <Button disabled={busy} onClick={() => run.mutate({ step: 'azure', input: { skip: true } })}>
                Skip the optional backend for now
              </Button>
            )}
          </div>
        )}

        {run.error && <MessageBar intent="error"><MessageBarBody>{(run.error as Error).message}</MessageBarBody></MessageBar>}

        <div className={styles.row} style={{ marginTop: tokens.spacingVerticalM }}>
          {s.status === 'active' && next && !packDrivesStep && (
            <Button appearance="primary" disabled={busy || !!needsCustomer} onClick={runNext}
                    title={needsCustomer ? 'Sign in the customer admin first' : undefined}>
              {busy ? 'Working…' : s.steps[next].status === 'failed' ? `Retry: ${STEP_LABELS[next]}` : `Run: ${STEP_LABELS[next]}`}
            </Button>
          )}
          {s.status === 'active' && <Button disabled={busy || abandon.isPending} onClick={() => abandon.mutate()}>Abandon</Button>}
          {s.status === 'completed' && done?.setupUrl && (
            <div className={styles.stack}>
              <MessageBar intent="success"><MessageBarBody>
                Done. Give the customer this setup link. {done.openAs} {done.whatHappens}
              </MessageBarBody></MessageBar>
              <div className={styles.row}>
                <Button as="a" appearance="primary" href={done.setupUrl} target="_blank" rel="noreferrer">Open setup</Button>
                <Text className={styles.msg}>{done.setupUrl}</Text>
              </div>
            </div>
          )}
          {s.azure?.consentTrim && !s.azure.consentTrim.removed && (
            <MessageBar intent="warning"><MessageBarBody>
              Deployment Manager could not remove its temporary Azure permissions ({s.azure.consentTrim.error ?? 'not checked'}). Ask the customer&apos;s admin to remove them:
              Microsoft Entra admin centre → Enterprise applications → <b>FirstIQ Customer Connect</b> → Permissions → revoke
              <b> Application.ReadWrite.All</b> (Microsoft Graph) and <b>user_impersonation</b> (Azure Service Management).{' '}
              <Link href="https://entra.microsoft.com/#view/Microsoft_AAD_IAM/StartboardApplicationsMenuBlade/~/AppAppsPreview" target="_blank" rel="noreferrer">Open Enterprise applications</Link>
            </MessageBarBody></MessageBar>
          )}
          {s.status === 'completed' && !done?.setupUrl && (
            <MessageBar intent="success"><MessageBarBody>
              Done. Open <Link href={s.siteUrl} target="_blank" rel="noreferrer">the site</Link> once so the product&apos;s setup wizard starts.
            </MessageBarBody></MessageBar>
          )}
        </div>
      </Card>
    </div>
  );
}
